// apps/web/src/features/commercial-proposals/useCommercialProposalForm.ts
//
// @aiPurpose form-state hook for the commercial-proposal editor (#1198).
//   Owns the editable `CommercialProposalFormState`, a single-field setter,
//   the live preview calc, and the `dirty` flag (form differs from the
//   baseline snapshot it was seeded with).
// @aiInvariant `dirty` compares the CURRENT form against the BASELINE form
//   captured at mount, so a revert-to-original is treated as clean — the
//   same revert-to-baseline rule the rest of the app's editors follow.
import { useMemo, useState } from 'react';
import {
  CommercialProposalCalculationInputSchema,
  CommercialProposalContentSchema,
  type CommercialProposalContent,
} from '@magimp/shared';

import { calculateCommercialProposalPreview, type CommercialProposalLiveCalc } from './calc.js';
import { formToCalcInput, formToContent, type CommercialProposalFormState } from './formModel.js';

export interface UseCommercialProposalFormResult {
  form: CommercialProposalFormState;
  setField: <K extends keyof CommercialProposalFormState>(
    key: K,
    value: CommercialProposalFormState[K],
  ) => void;
  dirty: boolean;
  valid: boolean;
  preview: CommercialProposalLiveCalc | null;
}

export function useCommercialProposalForm(
  initial: CommercialProposalFormState,
  prior: CommercialProposalContent | null = null,
): UseCommercialProposalFormResult {
  const [baseline] = useState(initial);
  const [form, setForm] = useState<CommercialProposalFormState>(initial);

  const setField = <K extends keyof CommercialProposalFormState>(
    key: K,
    value: CommercialProposalFormState[K],
  ): void => setForm((prev) => ({ ...prev, [key]: value }));

  const dirty = useMemo(() => JSON.stringify(form) !== JSON.stringify(baseline), [form, baseline]);

  // WHY: validity is derived from the shared content schema so the editor
  // never submits silently-coerced values; the live preview is computed ONLY
  // from a schema-valid calc input (otherwise `null`) so the manager never
  // sees totals derived from substituted defaults.
  // WHY (#1244 / D14): both run prior-aware (same `prior` the save uses) so an
  // UNTOUCHED divisor field — incl. a legacy divisor > 1 that displays a
  // negative percent — stays submittable + previewable instead of failing the
  // bounds it never crossed; an EDITED percent still goes through the D12
  // bounds (`prior` is null on the create host, preserving create defaults).
  const valid = useMemo(
    () => CommercialProposalContentSchema.safeParse(formToContent(form, prior)).success,
    [form, prior],
  );

  const preview = useMemo(() => {
    const parsed = CommercialProposalCalculationInputSchema.safeParse(formToCalcInput(form, prior));
    return parsed.success ? calculateCommercialProposalPreview(parsed.data) : null;
  }, [form, prior]);

  return { form, setField, dirty, valid, preview };
}
